import React, { useEffect, useState, useRef } from "react";
import { CheckCircle2, Circle, Loader2, AlertTriangle, XCircle } from "lucide-react";
import { api } from "../../lib/apiClient";
import { SecopSearchTrace } from "./SecopSearchTrace";

const ACTIVE_STATES = new Set(["nivel1_paid", "nivel1_processing"]);

const ICON = {
  done: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  running: <Loader2 className="w-4 h-4 text-primary animate-spin" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-600" />,
  failed: <XCircle className="w-4 h-4 text-rose-600" />,
  pending: <Circle className="w-4 h-4 text-gray-300" />,
};

function Step({ s, idx }) {
  return (
    <li
      className="relative pl-8 pb-5 last:pb-0 animate-in fade-in slide-in-from-left-2"
      style={{ animationDelay: `${idx * 60}ms` }}
      data-testid={`n1-progress-step-${s.key}`}
    >
      <span className="absolute left-0 top-0.5">{ICON[s.status] || ICON.pending}</span>
      <p className={`text-sm ${s.status === "pending" ? "text-gray-400" : "text-gray-900"} font-medium`}>{s.label}</p>
      {s.detail && <p className="text-xs text-gray-600 mt-0.5" data-testid={`n1-progress-detail-${s.key}`}>{s.detail}</p>}
      {s.key === "secop" && s.data?.steps?.length > 0 && <SecopSearchTrace steps={s.data.steps} compact />}
    </li>
  );
}

export function N1LiveProgress({ caseId, state, onFinished }) {
  const [data, setData] = useState(null);
  const sawRunningRef = useRef(false);

  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const { data: d } = await api.get(`/cases/${caseId}/n1/progress`);
        if (!alive) return;
        setData(d);
        const running = d?.progress?.status === "running" || ACTIVE_STATES.has(d?.state);
        if (running) {
          sawRunningRef.current = true;
          timer = setTimeout(tick, 3000);
        } else if (sawRunningRef.current) {
          sawRunningRef.current = false;
          onFinished && onFinished();
        }
      } catch {
        if (alive) timer = setTimeout(tick, 6000);
      }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [caseId, state, onFinished]);

  const p = data?.progress;
  if (!p && !ACTIVE_STATES.has(state)) return null;
  const running = p?.status === "running" || ACTIVE_STATES.has(data?.state || state);

  return (
    <section className="mt-6 border border-gray-200 bg-white p-6" data-testid="n1-live-progress">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-primary font-medium">Diagnóstico · cómo se está analizando tu caso</p>
          <p className="text-xs text-gray-500 mt-1">
            {running ? "En curso — se actualiza solo cada pocos segundos." : p?.status === "failed" ? "La generación se detuvo." : "Proceso completado."}
          </p>
        </div>
        {running && <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-1" data-testid="n1-progress-live-badge">En vivo</span>}
      </div>
      {!p ? (
        <p className="text-sm text-gray-600" data-testid="n1-progress-waiting">Esperando el inicio del análisis…</p>
      ) : (
        <ol className="relative border-l border-gray-200 ml-2 pl-0" data-testid="n1-progress-steps">
          {p.steps.map((s, i) => <Step key={s.key} s={s} idx={i} />)}
        </ol>
      )}
      {p?.status === "failed" && (
        <p className="mt-4 text-xs text-rose-700 bg-rose-50 border border-rose-200 p-3" data-testid="n1-progress-error">
          No se pudo completar el diagnóstico. El equipo fue notificado y lo reintentará; tus datos están guardados.
        </p>
      )}
    </section>
  );
}
